// Pure helpers for the SSNVS batch-detail results table. No Vue/DOM deps so
// they can be unit-tested directly (see tests/ssnvs-batch-detail-table.spec.js).

export function maskSsn(last4) {
    return `***-**-${last4 || ''}`
}

// SSNVS stores the submitted DOB snapshot as an 8-char MMDDYYYY string (or "").
// Render it as MM/DD/YYYY; anything unexpected falls back to an em dash.
export function formatDob(dob) {
    if (!dob || dob.length !== 8) return '—'
    return `${dob.slice(0, 2)}/${dob.slice(2, 4)}/${dob.slice(4, 8)}`
}

export function numericCodeCompare(a, b) {
    const na = parseFloat(a)
    const nb = parseFloat(b)
    const aNum = !isNaN(na)
    const bNum = !isNaN(nb)
    if (aNum && bNum && na !== nb) return na - nb
    if (aNum && !bNum) return -1
    if (!aNum && bNum) return 1
    return String(a).localeCompare(String(b))
}

// Status is a first-class concept: an unresolved problem, a resolved problem,
// a clean verification, or not-yet-checked. Ordered most-urgent first.
export const STATUS_ORDER = [
    { key: 'unresolved', title: 'Problems (Unresolved)' },
    { key: 'resolved', title: 'Problems (Resolved)' },
    { key: 'verified', title: 'Verified' },
    { key: 'pending', title: 'Pending' },
]

export function statusKey(result) {
    if (result.is_problem) return result.resolved ? 'resolved' : 'unresolved'
    if (!result.has_result) return 'pending'
    return 'verified'
}

// These three are always surfaced as sections/toggles even when empty, so the
// reviewer can always see (and re-show) them. Pending only appears when present.
const ALWAYS_SHOWN_STATUSES = ['unresolved', 'resolved', 'verified']

// The status categories to surface as sections and toggles, in canonical order
// with counts. Always includes the three mainline statuses (even at zero);
// Pending appears only when the batch actually has pending rows.
export function statusOptions(results) {
    const counts = {}
    results.forEach(r => {
        const key = statusKey(r)
        counts[key] = (counts[key] || 0) + 1
    })
    return STATUS_ORDER.filter(
        s => ALWAYS_SHOWN_STATUSES.includes(s.key) || counts[s.key]
    ).map(s => ({ key: s.key, title: s.title, count: counts[s.key] || 0 }))
}

export function groupResults(results, groupMode, visibleStatusKeys) {
    const visible = visibleStatusKeys ? new Set(visibleStatusKeys) : null

    if (groupMode === 'client') {
        const scoped = visible
            ? results.filter(r => visible.has(statusKey(r)))
            : results
        const byCode = new Map()
        scoped.forEach(r => {
            if (!byCode.has(r.client_code)) byCode.set(r.client_code, [])
            byCode.get(r.client_code).push(r)
        })
        return Array.from(byCode.keys())
            .sort(numericCodeCompare)
            .map(code => {
                const rows = byCode.get(code)
                const name = rows[0].client_name
                return {
                    id: `client-${code}`,
                    title: name ? `${code} — ${name}` : code,
                    results: rows,
                }
            })
    }

    // group by status: always render the mainline sections (even when empty),
    // hiding only the statuses whose toggle is unchecked
    return statusOptions(results)
        .filter(s => !visible || visible.has(s.key))
        .map(s => ({
            id: `status-${s.key}`,
            title: s.title,
            results: results.filter(r => statusKey(r) === s.key),
        }))
}

export function escapeHtml(s) {
    return String(s ?? '')
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
}
